import * as PIXI from 'pixi.js-legacy';
import { TextMetrics, TextStyle } from 'pixi.js-legacy';

PIXI.Container.defaultSortableChildren = true;

class AnimationTrack {
  constructor() {
    this._track = [];
    this._head = 0; // Index into _track set by seek().
  }

  getKeyframeValue(i) {
    return this._track[i * 2 + 1];
  }

  getKeyframeTime(i) {
    return this._track[i * 2];
  }

  addKeyframe(time, value) {
    this._track.push(time);
    this._track.push(value);
  }

  getLastKeyframeValue() {
    return this.getKeyframeValue(this.getKeyframeCount() - 1);
  }

  getLastKeyframeTime() {
    return this.getKeyframeTime(this.getKeyframeCount() - 1);
  }

  getKeyframeCount() {
    console.assert(this._track.length % 2 === 0);
    return this._track.length / 2;
  }

  seek(time) {
    // Binary search.
    //  O(log n), e.g. one additional step required for each doubling of n.
    let lo = 0;
    let hi = this.getKeyframeCount();
    function getMid() {
      return lo + Math.floor((hi - lo) / 2);
    }
    let mid = getMid();
    let count = 0;
    while (mid !== lo && count < 100) {
      count++;
      if (time <= this.getKeyframeTime(mid)) {
        hi = mid;
      } else if (time > this.getKeyframeTime(mid)) {
        lo = mid;
      } else break;
      mid = getMid();
    }
    this._head = mid;
  }

  getKeyframeAtHead() {
    return [this.getKeyframeValue(this._head), this.getKeyframeTime(this._head)];
  }

  getKeyframeValueAtHead() {
    return this.getKeyframeValue(this._head);
  }

  getKeyframeAfterHead() {
    if (this._head + 1 < this.getKeyframeCount()) {
      return [this.getKeyframeValue(this._head + 1), this.getKeyframeTime(this._head + 1)];
    } else {
      return this.getKeyframeAtHead();
    }
  }
}

export class Animation {
  constructor(type, name, creationStep, trackNames) {
    this.type = type;
    this.name = name;
    this.creationStep = creationStep;
    this.destructionStep = null;
    this.trackNames = trackNames;
    for (const trackName of this.trackNames) {
      this[trackName] = new AnimationTrack();
    }
  }

  getStartState() {
    const state = {};
    for (const key in this) {
      if (this[key] instanceof AnimationTrack) {
        state[key] = this[key].getKeyframeValue(0);
      } else {
        state[key] = this[key];
      }
    }
    return state;
  }
}

export class Rectangle extends PIXI.Graphics {
  constructor(startState) {
    super();
    if (startState.edge) {
      this.lineStyle(startState.edge, 0xffffff, 1);
    } else {
      this.beginFill(0xffffff);
    }
    // Using arc and lineTo rather than drawRoundedRect as they use the curve settings
    const halfWidth = startState.width / 2;
    const halfHeight = startState.height / 2;
    if (startState.radius) {
      const radius = Math.min(startState.radius, halfHeight, halfWidth);
      this.arc(halfWidth - radius, halfHeight - radius, radius, 0, Math.PI / 2);
      this.lineTo(radius - halfWidth, halfHeight);
      this.arc(radius - halfWidth, halfHeight - radius, radius, Math.PI / 2, Math.PI);
      this.lineTo(-halfWidth, radius - halfHeight);
      this.arc(radius - halfWidth, radius - halfHeight, radius, Math.PI, 1.5 * Math.PI);
      this.lineTo(halfWidth - radius, -halfHeight);
      this.arc(halfWidth - radius, radius - halfHeight, radius, 1.5 * Math.PI, 0);
      this.closePath();
    } else {
      this.drawRect(-halfWidth, -halfHeight, startState.width, startState.height);
    }
    this.endFill();
    this.name = startState.name;
    if (startState.draggable) this.dragging = false;
  }
}

export class Polygon extends PIXI.Graphics {
  constructor(startState) {
    super();
    if (startState.edge) {
      this.lineStyle(startState.edge, 0xffffff, 1);
    } else {
      this.beginFill(0xffffff);
    }
    this.drawPolygon(startState.points);
    this.polygonCenter = startState.polygonCenter;
    this.endFill();
    this.name = startState.name;
    if (startState.draggable) this.dragging = false;
  }
}

export class Circle extends PIXI.Graphics {
  constructor(startState, pixelsPerUnit) {
    super();
    if (startState.edge) {
      this.lineStyle(startState.edge, 0xffffff, 1);
    } else {
      this.beginFill(0xffffff);
    }
    // Using arc rather than drawCircle as arc uses the curve settings
    this.arc(0, 0, startState.radius, 0, 2 * Math.PI);
    this.closePath();
    this.endFill();
    this.name = startState.name;
    if (startState.draggable) this.dragging = false;
  }
}

export class ComplexShape extends PIXI.Graphics {
  constructor(startState) {
    super();
    if (startState.edge) {
      this.lineStyle(startState.edge, 0xffffff, 1);
    } else {
      this.beginFill(0xffffff);
    }
    this.moveTo(...startState.segments[0]);
    for (let i = 1; i < startState.segments.length; i++) {
      const seg = startState.segments[i];
      switch (seg.length) {
        case 2:
          this.lineTo(...seg);
          break;
        case 4:
          this.quadraticCurveTo(...seg);
          break;
        case 6:
          this.bezierCurveTo(...seg);
          break;
        default:
          // Skip malformed segments rather than crash
          break;
      }
    }
    if (startState.closed !== false) this.closePath();
    this.endFill();
    this.name = startState.name;
    if (startState.draggable) this.dragging = false;
  }
}

export class Bitmap extends PIXI.Sprite {
  constructor(startState, texture) {
    super(texture);
    this.width = startState.width * startState.scaleX;
    this.height = startState.height * startState.scaleY;
    this.anchor.x = 0.5;
    this.anchor.y = 0.5;
    this.name = startState.name;
    if (startState.draggable) this.dragging = false;
  }
}

// Text util class
class ScaledPoint {
  constructor(point, scale) {
    this._point = point;
    this._scale = scale;

    Object.defineProperty(this, 'x', {
      get: () => this._point.x * this._scale,
      set: (value) => (this._point.x = value / this._scale),
    });
    Object.defineProperty(this, 'y', {
      get: () => this._point.y * this._scale,
      set: (value) => (this._point.y = value / this._scale),
    });
  }

  set(x, y) {
    return this._point.set(x / this._scale, y / this._scale);
  }
}

// Since Text can be either PIXI.Text or PIXI.HTMLText it does not extend anything or have a constructor
export class Text {
  static createText(startState, pixelsPerUnit) {
    /* The font tends to get squished at very small sizes, in around 1.0.
           To avoid this we're rendering it at a larger size and then scale it
           down via custom properties on the scale object. */
    let child;
    const resolution = pixelsPerUnit;
    const fontSize = startState.size * resolution;
    const fontFamily = 'Roboto, sans-serif';
    const style = {
      fontSize: fontSize,
      fontFamily: fontFamily,
      fill: 0xffffff,
      align: startState.textAlign,
    };
    if (startState.isHTML === true) child = new PIXI.HTMLText(startState.text, style);
    else child = new PIXI.Text(startState.text, style);

    child.anchor.x = startState.textAlign === 'left' ? 0 : startState.textAlign === 'center' ? 0.5 : 1;
    child.anchor.y = 0.5;
    child.resolution = 2.0;
    child._facadeScale = new ScaledPoint(child.scale, resolution);
    Object.defineProperty(child, 'scale', {
      get: function () {
        return this._facadeScale;
      },
      set: function (point) {
        this._facadeScale.set(point.x, point.y);
      },
    });
    child.name = startState.name;
    if (startState.draggable) child.dragging = false;
    return child;
  }
}

export class Spritesheet extends PIXI.AnimatedSprite {
  constructor(startState, textures) {
    super(textures);
    this.width = startState.width * startState.scaleX;
    this.height = startState.height * startState.scaleY;
    this.anchor.x = 0.5;
    this.anchor.y = 0.5;
  }

  static fromSheetImage(startState, sheetImage) {
    const textures = [];

    if (!sheetImage.sheetData || !sheetImage.sheetData.animations) {
      console.error('Sprite sheet ' + startState.image + ' missing animation data');
      textures.push(new PIXI.Texture(sheetImage.tex.baseTexture));
    } else {
      const spriteSheetTexture = sheetImage.tex.baseTexture;
      const sheetData = sheetImage.sheetData;
      const animationName = startState.animation;
      let frameOrder =
        sheetData.animations[animationName] || sheetData.animations[Object.keys(sheetData.animations)[0]];

      // Set none default frames
      if (startState.frameIndexStart !== undefined || startState.frameIndexEnd !== undefined) {
        const frameStart = startState.frameIndexStart || 0;
        const frameEnd = startState.frameIndexEnd !== undefined ? startState.frameIndexEnd : frameOrder.length;
        if (frameStart <= frameEnd) {
          frameOrder = frameOrder.slice(frameStart, frameEnd + 1);
        } else {
          // Reverse animation
          frameOrder = frameOrder.slice(frameEnd, frameStart + 1);
          frameOrder.reverse();
        }
      }

      // Get individual textures from spritesheet
      for (let frameIndex = 0; frameIndex < frameOrder.length; frameIndex++) {
        const spriteData = sheetData.frames[frameOrder[frameIndex]];
        if (spriteData.trimmed) {
          textures.push(
            new PIXI.Texture(
              spriteSheetTexture,
              new PIXI.Rectangle(spriteData.frame.x, spriteData.frame.y, spriteData.frame.w, spriteData.frame.h), // frame
              new PIXI.Rectangle(0, 0, spriteData.sourceSize.w, spriteData.sourceSize.h), // original size
              new PIXI.Rectangle(
                spriteData.spriteSourceSize.x,
                spriteData.spriteSourceSize.y,
                spriteData.spriteSourceSize.w,
                spriteData.spriteSourceSize.h
              )
            )
          );
        } else {
          textures.push(
            new PIXI.Texture(
              spriteSheetTexture,
              new PIXI.Rectangle(spriteData.frame.x, spriteData.frame.y, spriteData.frame.w, spriteData.frame.h)
            )
          );
        }
      }
    }
    let newSpriteSheet = new Spritesheet(startState, textures);
    newSpriteSheet.name = startState.name;
    if (startState.draggable) newSpriteSheet.dragging = false;
    return newSpriteSheet;
  }
}

export class Container extends PIXI.Container {
  constructor(startState) {
    super();
    this.name = startState.name;
    if (startState.draggable) this.dragging = false;
  }
}

export class ScrollArea extends PIXI.Container {
  constructor(startState) {
    super();
    // Used to prevent bad hover interactions.
    this.isViewArea = true;
    // Prevents content outside the scroll area from being rendered.
    this.mask = new PIXI.Graphics()
      .beginFill(0xffffff)
      .drawRect(-startState.width / 2, -startState.height / 2, startState.width, startState.height)
      .endFill();
    this.addChild(this.mask);
    // Ensures that scrolling works even where content is missing.
    const back = new PIXI.Graphics()
      .beginFill(0xffffff)
      .drawRect(-startState.width / 2, -startState.height / 2, startState.width, startState.height)
      .endFill();
    back.alpha = 0;
    this.addChild(back);
    // Container for children (the content).
    this.content = new PIXI.Container();
    this.addChild(this.content);
    // The horizontal scrollbar.
    this.xThumb = new PIXI.Graphics()
      .beginFill(0xffffff)
      .drawRect(0, -startState.thickness, startState.width, startState.thickness)
      .endFill();
    this.xThumb.x = -startState.width / 2;
    this.xThumb.y = startState.height / 2;
    this.xThumb.alpha = 0.5;
    this.addChild(this.xThumb);
    // The vertical scrollbar.
    this.yThumb = new PIXI.Graphics()
      .beginFill(0xffffff)
      .drawRect(-startState.thickness, 0, startState.thickness, startState.height)
      .endFill();
    this.yThumb.x = startState.width / 2;
    this.yThumb.y = -startState.height / 2;
    this.yThumb.alpha = 0.5;
    this.addChild(this.yThumb);
    this.widgets = [this.xThumb, this.yThumb];
    this.name = startState.name;
    if (startState.draggable) this.dragging = false;
  }
}

export class HTMLArea extends ScrollArea {
  constructor(startState, pixelsPerUnit, html, isDarkMode) {
    super(startState);

    const resolution = pixelsPerUnit;
    const fontSize = startState.size * resolution;
    const fontFamily = 'Roboto, sans-serif';
    const style = {
      fontSize: fontSize,
      fontFamily: fontFamily,
      fill: isDarkMode ? 0xffffff : 0x000000,
      align: startState.textAlign,
      wordWrap: true,
      wordWrapWidth: 0.95 * startState.width * resolution,
    };
    this.html = new PIXI.HTMLText(html, style);
    this.html.scale.x = 1 / resolution;
    this.html.scale.y = 1 / resolution;
    this.content.addChild(this.html);
  }
}

export class InputTextField extends PIXI.Container {
  constructor(startState, pixelsPerUnit, isDarkMode) {
    super();

    // Calculate dark and light theme colors.
    const mode_color = (color) => {
      if (typeof color !== 'string') return color;
      const parts = color.split('|', 2);
      const ret_val = isDarkMode && parts.length > 1 ? parts[1] : parts[0];
      return ret_val;
    };
    const background_color = mode_color(startState.color);
    const active_background_color = mode_color(startState.active_background_color);
    const border_color = mode_color(startState.border_color);
    const text_color = mode_color(startState.text_color);
    const default_text_color = mode_color(startState.default_text_color);

    // Define custom event 'submitEvent', 'mouseEvents' and register callback functions.
    this._events = new PIXI.utils.EventEmitter();
    this._onsubmitted = null;
    Object.defineProperty(this, 'onsubmitted', {
      get: function () {
        return this._onsubmitted;
      },
      set: function (callback) {
        if (typeof callback === 'function') {
          this._onsubmitted = callback;
          this._events.on('submitEvent', this._onsubmitted, this);
        }
      },
    });

    this._onmouseevents = null;
    Object.defineProperty(this, 'onmouseevents', {
      get: function () {
        return this._onmouseevents;
      },
      set: function (callback) {
        if (typeof callback === 'function') {
          this._onmouseevents = callback;
          this._events.on('mouseEvent', this._onmouseevents, this);
        }
      },
    });

    // Set initial properties of the container.
    this.height = startState.height + startState.edge * 2;
    this.width = startState.width + startState.edge * 2;
    this.position.set(startState.x, startState.y);
    this.name = startState.name;
    this._isActiveInteract = false;
    Object.defineProperty(this, 'isActiveInteract', {
      get: function () {
        return this._isActiveInteract;
      },
      set: function (active_interact) {
        this._isActiveInteract = active_interact;
        this.domField.disabled = !active_interact;
      },
    });

    const resolution = pixelsPerUnit;
    const fontSize = startState.height * 0.7 * resolution;
    const fontFamily = 'Roboto, sans-serif';

    const style = new TextStyle({
      fontSize: fontSize,
      fill: default_text_color,
      fontFamily: fontFamily,
      align: 'left',
    });

    // InputTextField will be created with default text provided.
    this.pixi_text = new PIXI.Text(startState.default_text, style);
    this.pixi_text.anchor.x = 0;
    this.pixi_text.anchor.y = 0.5;
    this.pixi_text.resolution = 2;
    this.pixi_text._facadeScale = new ScaledPoint(this.pixi_text.scale, resolution);
    Object.defineProperty(this.pixi_text, 'scale', {
      get: function () {
        return this._facadeScale;
      },
      set: function (point) {
        this._facadeScale.set(point.x, point.y);
      },
    });

    // Map to keep track of char width by index.
    this.char_widths = [];

    // Calculate initial values.
    const right_padding = startState.height * 0.1;
    const cursor_pos_y = startState.height * 0.1;
    const cursor_height = startState.height * 0.8;
    const cursor_width = startState.height * 0.04; // With paddings
    const actual_cursor_width = cursor_width / 2; // Cursor line width
    const cursor_pos_start_x = right_padding;
    const text_mask_width = startState.width - cursor_width - 2 * right_padding;

    // Keep track of the index of the character where the cursor is positioned.
    this.cursor_current_index = 0;

    // Keep track of the new index of the character where the cursor should be positioned.
    this.cursor_new_index = 0;

    // Update default text position and scale down to 1.
    this.pixi_text.position.set(right_padding, startState.height / 2);
    this.pixi_text.scale.x = 1;
    this.pixi_text.scale.y = 1;

    // text_mask is used to cull the text view if it's length goes beyond the length of the text field.
    this.text_mask = new PIXI.Graphics();
    this.text_mask.beginFill(0xffffff);
    this.text_mask.drawRect(right_padding, 0, text_mask_width, startState.height);
    this.text_mask.endFill();
    this.addChild(this.text_mask);
    this.pixi_text.mask = this.text_mask;

    // Create a Pixi line segment as the cursor.
    this.cursorLine = new PIXI.Graphics();
    this.cursorLine.lineStyle(actual_cursor_width, text_color);
    this.cursorLine.moveTo(0, 0);
    this.cursorLine.lineTo(0, cursor_height);
    this.cursorLine.position.set(cursor_pos_start_x, cursor_pos_y);
    this.cursorLine.visible = false;

    // Create the background to show when the text field is focused.
    this.backgroundFocused = new PIXI.Graphics();
    this.backgroundFocused.beginFill(active_background_color);
    this.backgroundFocused
      .lineStyle(startState.edge * 1.5, border_color, 1)
      .drawRoundedRect(0, 0, startState.width, startState.height, startState.radius || 0)
      .endFill();
    this.backgroundFocused.visible = false;
    this.backgroundFocused.eventMode = 'static';
    this.backgroundFocused.cursor = 'text';

    // Create the background to show when the text field is not focused.
    this.background = new PIXI.Graphics();
    this.background.beginFill(background_color);
    this.background
      .lineStyle(startState.edge, border_color, 1)
      .drawRoundedRect(0, 0, startState.width, startState.height, startState.radius || 0)
      .endFill();

    // DOM input field to capture user inputs.
    this.domField = document.createElement('input');
    this.domField.type = 'text';
    this.domField.style.position = 'absolute';
    document.body.appendChild(this.domField);
    this.domField.style.top = 0;
    this.domField.style.left = 0;
    this.domField.zIndex = -1;
    this.focused = false;
    this.domField.disabled = true;

    this.addChild(this.background);
    this.addChild(this.backgroundFocused);
    this.addChild(this.pixi_text);
    this.addChild(this.cursorLine);

    // Function to set text externally
    this.setText = function (text_str) {
      style.fill = text_color;
      this.pixi_text.style = style;

      this.domField.value = text_str;
      this.pixi_text.text = this.domField.value;

      // Reset cursor position.
      this.cursorLine.position.set(cursor_pos_start_x, cursor_pos_y);
      this.cursor_current_index = 0;
      this.char_widths = [];

      var new_char_count = this.domField.value.length;
      for (let index = 0; index < new_char_count; index++) {
        var m = TextMetrics.measureText(this.domField.value[this.cursor_current_index + index], style);
        this.char_widths.splice(this.cursor_current_index + index, 0, m.width / resolution);
      }
      this.cursor_new_index = this.cursor_current_index + new_char_count;
      updateTextGraphics(KeyEvent.NEW_CHAR);
    };

    Object.defineProperty(this, 'text', {
      get: function () {
        return this.pixi_text.text;
      },
      set: function (text) {
        if (text) {
          this.setText(text);
        }
      },
    });

    // Function to blink the cursor.
    const blinkCursor = () => {
      if (this.backgroundFocused.visible) {
        this.cursorLine.visible = !this.cursorLine.visible;
      }
      setTimeout(blinkCursor, 500);
    };

    // Start the blinking animation
    blinkCursor();

    this.background.eventMode = 'static';
    var self = this;

    function setDefaultText() {
      style.fill = default_text_color;
      self.pixi_text.style = style;
      self.pixi_text.text = startState.default_text;
    }

    function click(event) {
      if (!(self._events instanceof PIXI.utils.EventEmitter)) return;
      self.domField.focus();
      self._events.emit('mouseEvent', self.name, self.domField.value, event);
    }

    function onFocus(event) {
      if (!(self._events instanceof PIXI.utils.EventEmitter)) return;
      self.backgroundFocused.visible = true;
      self.cursorLine.visible = true;
      self.pixi_text.text = self.domField.value;
      self._events.emit('mouseEvent', self.name, self.domField.value, event);
    }

    function onBlur(event) {
      if (!(self._events instanceof PIXI.utils.EventEmitter)) return;
      self.backgroundFocused.visible = false;
      self.cursorLine.visible = false;
      if (self.domField.value.length === 0) {
        setDefaultText();
      }
      self._events.emit('mouseEvent', self.name, self.domField.value, event);
    }

    this.background.on('click', click);
    this.background.touchstart = click;

    self.domField.onfocus = onFocus;
    self.domField.onblur = onBlur;

    self.domField.onkeypress = function (event) {
      if (!self._events) return;
      if (event.code === 'Enter' || event.code === 'NumpadEnter') {
        self._events.emit('submitEvent', self.name, self.domField.value);
      }
    };

    const KeyEvent = {
      ARROW_LEFT: 0,
      ARROW_RIGHT: 1,
      DELETE: 2,
      BACKSPACE: 3,
      NEW_CHAR: 4,
    };

    function updateTextGraphics(keyEvent) {
      const sumOfSubarray = (arr, start, end) => arr.slice(start, end + 1).reduce((acc, current) => acc + current, 0);
      var width_diff = 0;
      var new_cursor_pos_x = self.cursorLine.position.x;

      switch (keyEvent) {
        case KeyEvent.ARROW_LEFT:
          if (self.cursor_new_index < self.cursor_current_index) {
            width_diff -= self.char_widths[self.cursor_current_index - 1];
            new_cursor_pos_x = width_diff + self.cursorLine.position.x;
          }
          break;
        case KeyEvent.ARROW_RIGHT:
          if (self.cursor_new_index > self.cursor_current_index) {
            width_diff = self.char_widths[self.cursor_current_index];
            new_cursor_pos_x = width_diff + self.cursorLine.position.x;
          }
          break;
        case KeyEvent.BACKSPACE:
          if (self.cursor_current_index > self.cursor_new_index) {
            var ret_bs = self.char_widths.splice(self.cursor_new_index, 1);
            if (ret_bs.length === 1) width_diff -= ret_bs[0];
            new_cursor_pos_x = width_diff + self.cursorLine.position.x;
          }
          self.pixi_text.text = self.domField.value;
          break;
        case KeyEvent.DELETE:
          var ret_del = self.char_widths.splice(self.cursor_new_index, 1);
          if (ret_del.length === 1) width_diff = ret_del[0];
          self.pixi_text.text = self.domField.value;
          break;
        case KeyEvent.NEW_CHAR:
          width_diff = sumOfSubarray(self.char_widths, self.cursor_current_index, self.cursor_new_index - 1);
          self.pixi_text.text = self.domField.value;
          new_cursor_pos_x = width_diff + self.cursorLine.position.x;
          break;
        default:
          break;
      }

      // Calculate current text width.
      const current_text_width = sumOfSubarray(self.char_widths, 0, self.char_widths.length);
      self.cursor_current_index = self.cursor_new_index;

      /* If the newly calculated cursor position is outside the bound of the box on left side,
          move the text to the right side.*/
      if (new_cursor_pos_x < cursor_pos_start_x) {
        if (self.cursor_current_index === 0) {
          new_cursor_pos_x = cursor_pos_start_x;
          self.pixi_text.position.x = right_padding;
        } else {
          // Move the Pixi text right to display previous character.
          self.pixi_text.position.x -= width_diff;
          new_cursor_pos_x = self.cursorLine.position.x;
        }
      }

      /* If the newly calculated cursor position is outside the bound of the box on right side,
          move the text to the left side.*/
      if (new_cursor_pos_x > text_mask_width + right_padding) {
        if (self.cursor_current_index === self.domField.value.length) {
          new_cursor_pos_x = text_mask_width + right_padding;
          let diff = current_text_width - text_mask_width;
          self.pixi_text.position.x = right_padding - diff;
        } else {
          // Move pixi text right to display previous character.
          self.pixi_text.position.x -= width_diff;
          new_cursor_pos_x = self.cursorLine.position.x;
        }
      }
      self.cursorLine.position.x = new_cursor_pos_x;
    }

    this.domField.onkeydown = function (event) {
      // Get the width of the passed char and positioned cursor on new position.
      switch (event.code) {
        case 'ArrowLeft':
          self.cursor_new_index = self.cursor_current_index > 0 ? self.cursor_current_index - 1 : 0;
          updateTextGraphics(KeyEvent.ARROW_LEFT);
          break;
        case 'ArrowRight':
          self.cursor_new_index =
            self.cursor_current_index < self.domField.value.length
              ? self.cursor_current_index + 1
              : self.domField.value.length;
          updateTextGraphics(KeyEvent.ARROW_RIGHT);
          break;
        case 'Backspace':
          self.cursor_new_index = self.cursor_current_index > 0 ? self.cursor_current_index - 1 : 0;
          updateTextGraphics(KeyEvent.BACKSPACE);
          break;
        case 'Delete':
          self.cursor_new_index = self.cursor_current_index;
          updateTextGraphics(KeyEvent.DELETE);
          break;
        default:
          break;
      }
    };

    self.domField.onkeyup = function (event) {
      // Check if DOM input text changed.
      if (self.pixi_text.text === self.domField.value) {
        return;
      }

      // Left and Right Arrow keys are handled separately.
      switch (event.code) {
        case 'ArrowLeft':
        case 'ArrowRight':
        case 'Backspace':
        case 'Delete':
          self.pixi_text.text = self.domField.value;
          return 0;
        default:
          break;
      }

      // Change text color.
      if (style.fill.toLocaleLowerCase() === default_text_color.toLocaleLowerCase()) {
        style.fill = text_color;
        self.pixi_text.style = style;
      }

      //DOM input can have multiple characters.
      var new_char_count = self.domField.value.length - self.pixi_text.text.length;
      for (let index = 0; index < new_char_count; index++) {
        var m = TextMetrics.measureText(self.domField.value[self.cursor_current_index + index], style);
        self.char_widths.splice(self.cursor_current_index + index, 0, m.width / resolution);
      }

      self.cursor_new_index = self.cursor_current_index + new_char_count;
      updateTextGraphics(KeyEvent.NEW_CHAR);
    };
  }
}

// Replacement for the default EventBoundary which takes into account our isStandalone property
// TODO: Figure out a way to not trigger effects of parents of standalone elements.
export class ModifiedEventBoundary extends PIXI.EventBoundary {
  // Changes from original method marked with CHANGE
  hitTestMoveRecursive(currentTarget, eventMode, location, testFn, pruneFn, ignore = false) {
    let shouldReturn = false;

    // only bail out early if it is not interactive
    if (this._interactivePrune(currentTarget)) return null;

    // CHANGE: Skip the following check as we do not use dynamic event modes at the moment
    // if (currentTarget.eventMode === 'dynamic' || eventMode === 'dynamic') {
    //   EventsTicker.pauseUpdate = false;
    // }

    if (currentTarget.interactiveChildren && currentTarget.children) {
      const children = currentTarget.children;

      for (let i = children.length - 1; i >= 0; i--) {
        const child = children[i];

        const nestedHit = this.hitTestMoveRecursive(
          child,
          this._isInteractive(eventMode) ? eventMode : child.eventMode,
          location,
          testFn,
          pruneFn,
          ignore || pruneFn(currentTarget, location)
        );

        if (nestedHit) {
          // Its a good idea to check if a child has lost its parent.
          // this means it has been removed whilst looping so its best
          if (nestedHit.length > 0 && !nestedHit[nestedHit.length - 1].parent) {
            continue;
          }

          // CHANGE: Ignore this child if it is non-interactive and standalone
          if (nestedHit.length === 0 && child.isStandalone) continue;

          // Only add the current hit-test target to the hit-test chain if the chain
          // has already started (i.e. the event target has been found) or if the current
          // target is interactive (i.e. it becomes the event target).
          const isInteractive = currentTarget.isInteractive();

          if (nestedHit.length > 0 || isInteractive) {
            if (isInteractive) this._allInteractiveElements.push(currentTarget);
            nestedHit.push(currentTarget);
          }

          // store all hit elements to be returned once we have traversed the whole tree
          if (this._hitElements.length === 0) this._hitElements = nestedHit;

          shouldReturn = true;
        }
      }
    }

    const isInteractiveMode = this._isInteractive(eventMode);
    const isInteractiveTarget = currentTarget.isInteractive();

    if (isInteractiveMode && isInteractiveTarget) this._allInteractiveElements.push(currentTarget);

    // we don't carry on hit testing something once we have found a hit,
    // now only care about gathering the interactive elements
    if (ignore || this._hitElements.length > 0) return null;

    if (shouldReturn) return this._hitElements;

    // Finally, hit test this DisplayObject itself.
    if (isInteractiveMode && !pruneFn(currentTarget, location) && testFn(currentTarget, location)) {
      // The current hit-test target is the event's target only if it is interactive. Otherwise,
      // the first interactive ancestor will be the event's target.
      return isInteractiveTarget ? [currentTarget] : [];
    }

    return null;
  }

  // Changes from original method marked with CHANGE
  hitTestRecursive(currentTarget, eventMode, location, testFn, pruneFn) {
    // Attempt to prune this DisplayObject and its subtree as an optimization.
    if (this._interactivePrune(currentTarget) || pruneFn(currentTarget, location)) {
      return null;
    }

    // CHANGE: Skip the following check as we do not use dynamic event modes at the moment
    // if (currentTarget.eventMode === 'dynamic' || eventMode === 'dynamic') {
    //   EventsTicker.pauseUpdate = false;
    // }

    // Find a child that passes the hit testing and return one, if any.
    if (currentTarget.interactiveChildren && currentTarget.children) {
      const children = currentTarget.children;

      for (let i = children.length - 1; i >= 0; i--) {
        const child = children[i];

        const nestedHit = this.hitTestRecursive(
          child,
          this._isInteractive(eventMode) ? eventMode : child.eventMode,
          location,
          testFn,
          pruneFn
        );

        if (nestedHit) {
          // Its a good idea to check if a child has lost its parent.
          // this means it has been removed whilst looping so its best
          if (nestedHit.length > 0 && !nestedHit[nestedHit.length - 1].parent) {
            continue;
          }

          // CHANGE: Ignore this child if it is non-interactive and standalone
          if (nestedHit.length === 0 && child.isStandalone) continue;

          // Only add the current hit-test target to the hit-test chain if the chain
          // has already started (i.e. the event target has been found) or if the current
          // target is interactive (i.e. it becomes the event target).
          const isInteractive = currentTarget.isInteractive();

          if (nestedHit.length > 0 || isInteractive) nestedHit.push(currentTarget);

          return nestedHit;
        }
      }
    }

    const isInteractiveMode = this._isInteractive(eventMode);
    const isInteractiveTarget = currentTarget.isInteractive();

    // Finally, hit test this DisplayObject itself.
    if (isInteractiveMode && testFn(currentTarget, location)) {
      // The current hit-test target is the event's target only if it is interactive. Otherwise,
      // the first interactive ancestor will be the event's target.
      return isInteractiveTarget ? [currentTarget] : [];
    }

    return null;
  }
}
